'use client';

import { useState } from 'react';
import { Table, Grid3X3, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { JsonViewer } from '@/components/data/json-viewer';
import type { ExecutionResult } from '@/lib/integrations/types';

interface DataTabProps {
  result: ExecutionResult;
}

export function DataTab({ result }: DataTabProps) {
  const [viewMode, setViewMode] = useState<'json' | 'table'>('json');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Detect if data is array for table view option
  const isArray = Array.isArray(result.data);
  const dataArray = isArray ? result.data as Record<string, unknown>[] : null;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'json' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('json')}
          >
            <Grid3X3 className="h-4 w-4 mr-1" />
            JSON
          </Button>
          {isArray && (
            <Button
              variant={viewMode === 'table' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <Table className="h-4 w-4 mr-1" />
              Table
            </Button>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={handleCopy}>
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-1 text-green-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1" />
              Copy JSON
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      {viewMode === 'json' ? (
        <div className="bg-muted/50 rounded-lg p-4 max-h-[600px] overflow-auto">
          <JsonViewer data={result.data} />
        </div>
      ) : (
        dataArray && (
          <div className="border rounded-lg overflow-auto max-h-[600px]">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  {dataArray[0] &&
                    Object.keys(dataArray[0]).slice(0, 8).map(key => (
                      <th
                        key={key}
                        className="px-3 py-2 text-left font-medium text-muted-foreground"
                      >
                        {key}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {dataArray.slice(0, 100).map((row, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    {Object.entries(row).slice(0, 8).map(([key, value]) => (
                      <td key={key} className="px-3 py-2 font-mono text-xs truncate max-w-48">
                        {typeof value === 'object'
                          ? JSON.stringify(value).slice(0, 50)
                          : String(value ?? '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {dataArray.length > 100 && (
              <div className="text-center py-2 text-xs text-muted-foreground bg-muted/30">
                Showing first 100 of {dataArray.length} rows
              </div>
            )}
          </div>
        )
      )}

      {/* Data Stats */}
      <div className="text-xs text-muted-foreground">
        {isArray ? (
          <span>{(result.data as unknown[]).length} items</span>
        ) : (
          <span>
            {typeof result.data === 'object' && result.data
              ? `${Object.keys(result.data).length} fields`
              : typeof result.data}
          </span>
        )}
      </div>
    </div>
  );
}

export default DataTab;
